import { StockThresholdsService } from '@/api/profile'
import { AppService } from '@/app/shared/services/app.service'
import { Roles, UserService } from '@/app/shared/services/user.service'
import { Modal } from '@/app/v2/shared/modal/modal'
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, effect, inject, resource } from '@angular/core'
import { ActivatedRoute, RouterOutlet } from '@angular/router'
import { Toast } from 'app/shared/components/toast/toast'
import { NavLink, SideNav } from '@/app/v2/shared/components/layout/sidenav/sidenav'
import { firstValueFrom } from 'rxjs'
import { CommandPalette } from '@/app/v2/app/command-palette/command-palette'
import { InventoryBanner } from '@/app/v2/app/inventories/inventory-banner'

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-base-layout',
  imports: [CommandPalette, SideNav, RouterOutlet, Toast, Modal, NgComponentOutlet, NgTemplateOutlet, InventoryBanner],
  templateUrl: './base-layout.html',
})
export class BaseLayout {
  route = inject(ActivatedRoute)
  user = inject(UserService)
  app = inject(AppService)
  private readonly stockThresholds = inject(StockThresholdsService)

  home = '/'

  // Nombre de lignes proposées en réappro (pastille du menu), calculé pour le stock courant.
  // La garde doit rester alignée sur les rôles de ReplenishmentProposalStockThresholdUseCase : appeler
  // l'endpoint sans en avoir le droit récolterait un 403 à chaque changement de stock. GlobalReader y
  // figure désormais — sans lui, il voyait l'entrée « Réappro auto » sans jamais sa pastille.
  readonly replenishmentCount = resource({
    params: () => ({
      id: this.app.stockLocation()?.id ?? null,
      canReadProposal:
        this.user.hasRole(Roles.StockManager) ||
        this.user.hasRole(Roles.GlobalAdmin) ||
        this.user.hasRole(Roles.CPO) ||
        this.user.hasRole(Roles.GlobalReader),
    }),
    loader: async ({ params }) => {
      if (!params.id || !params.canReadProposal) return 0
      const res = await firstValueFrom(this.stockThresholds.replenishmentProposal({ stockLocationId: params.id }))
      return res.lines?.length ?? 0
    },
    defaultValue: 0,
  })

  // Menu = DÉRIVATION pure (rôles + compteur réappro) : computed, pas d'effect qui pousse un signal.
  readonly links = computed<NavLink[]>(() => {
    const newLinks: NavLink[] = []

    const addLink = (link: NavLink) => {
      // Dédup par URL pour les liens simples ; les groupes (sans URL) sont toujours ajoutés.
      if (link.url && newLinks.some((existingLink) => existingLink.url === link.url)) return
      newLinks.push(link)
    }

      const replenishmentCount = this.replenishmentCount.value()
      // Les entrées PUREMENT de mutation (ex. Sortie en masse) sont retirées pour un lecteur pur (GlobalReader).
      const canWrite = this.user.canWrite()

      if (
        this.user.hasRole(Roles.GlobalAdmin) ||
        this.user.hasRole(Roles.GlobalReader) ||
        this.user.hasRole(Roles.ProcurementManager) ||
        this.user.hasRole(Roles.StockManager) ||
        // O13 : le CPO a la lecture (scope Company) sur les référentiels appro/stock/traçabilité côté backend.
        this.user.hasRole(Roles.CPO)
      ) {
        addLink({
          name: 'Approvisionnement',
          shortName: 'Appro',
          icon: 'shopping_cart',
          nestedLinks: [
            { name: 'Articles', icon: 'view_in_ar', url: 'products' },
            { name: 'Demandes', icon: 'assignment', url: 'requisitions' },
            { name: 'Commandes', icon: 'shopping_cart_checkout', url: 'orders' },
            { name: 'Réceptions', shortName: 'Récept.', icon: 'move_to_inbox', url: 'receipts' },
            { name: 'Retours', icon: 'assignment_return', url: 'returns' },
            { name: 'Réappro auto', shortName: 'Réappro', icon: 'autorenew', url: 'auto-replenishment', badge: replenishmentCount },
          ],
        })
        addLink({
          name: 'Stock',
          icon: 'inventory_2',
          nestedLinks: [
            { name: 'Stocks', icon: 'warehouse', url: 'stock-levels' },
            { name: 'Mouvements', shortName: 'Mouv.', icon: 'swap_vert', url: 'stock-movements' },
            ...(canWrite ? [{ name: 'Sortie en masse', shortName: 'Sortie', icon: 'logout', url: 'bulk-stock-out' }] : []),
            { name: 'Réservations', shortName: 'Réserv.', icon: 'bookmark_added', url: 'stock-reservations' },
            { name: 'Min/Max', icon: 'tune', url: 'min-max' },
          ],
        })
        addLink({
          name: 'Traçabilité',
          shortName: 'Traça',
          icon: 'fingerprint',
          nestedLinks: [
            { name: 'Appareils', icon: 'devices', url: 'devices' },
            { name: 'N° série/lot', shortName: 'Série/lot', icon: 'qr_code_2', url: 'traceabilities' },
          ],
        })
      }

      if (this.user.user()?.roles.some((r) => ['GlobalAdmin', 'GlobalReader', 'CompanyAdmin', 'DivisionAdmin', 'profile_Admin', 'profile_StockManager', 'profile_ProcurementManager', 'profile_CPO', 'profile_InventoryController'].includes(r))) {
        addLink({ name: 'Inventaires', icon: 'fact_check', url: 'inventories' })
      }

      // Qualité : consultation des non-conformités. GlobalReader et GlobalAdmin voient tout ; le
      // gestionnaire de stock y accède aussi, mais ProFile ne lui remonte que les non-conformités
      // rattachables à une commande ou à un retour de SES lieux (le filtrage est côté backend).
      // Doit rester aligné sur nonConformityGuard, qui garde la route.
      if (this.user.hasRole(Roles.GlobalReader) || this.user.hasRole(Roles.GlobalAdmin) || this.user.hasRole(Roles.StockManager) || this.user.hasRole(Roles.CPO)) {
        addLink({
          name: 'Qualité',
          shortName: 'Qualité',
          icon: 'rule',
          nestedLinks: [{ name: 'Non-conformités', shortName: 'NC', icon: 'report_problem', url: 'non-conformities' }],
        })
      }

      // Administration : réservé aux GlobalAdmin.
      if (this.user.hasRole(Roles.GlobalAdmin)) {
        addLink({
          name: 'Administration',
          shortName: 'Admin',
          icon: 'admin_panel_settings',
          nestedLinks: [
            { name: 'Synchronisations', shortName: 'Synchro', icon: 'sync', url: 'admin/sync' },
            { name: 'Codes de rejet', shortName: 'Codes rejet', icon: 'rule', url: 'admin/rejection-codes' },
            { name: 'Utilisateurs', shortName: 'Utilisateurs', icon: 'group', url: 'admin/users' },
          ],
        })
      }

    return newLinks
  })

  constructor() {
    // Effect légitime : document.title est une API non réactive.
    effect(() => {
      const child = this.route.firstChild?.snapshot.routeConfig?.path
      document.title = child ? `Proteor • ${child}` : 'Proteor'
    })
  }
}
